import React, { useEffect, useState, useRef } from 'react';
import withAuth from '../../auth/WithAuth';
import './myproposals.scss';
import FurnitureNavbar from '../../../components/layout/Nav';
import { Badge, Modal, Button, Carousel } from 'react-bootstrap';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import { FaComments } from 'react-icons/fa';

const MyProposals = () => {
  const [proposals, setProposals] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filter, setFilter] = useState('All');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  // Negotiation chat
  const [negotiationProposal, setNegotiationProposal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [finalAgreement, setFinalAgreement] = useState(null);
  const [finalAgreementAmount, setFinalAgreementAmount] = useState('');
  const [isSubmittingFinalAgreement, setIsSubmittingFinalAgreement] =
    useState(false);
  const [showFinalAgreementForm, setShowFinalAgreementForm] = useState(false);
  const [editingFinalAgreement, setEditingFinalAgreement] = useState(false);
  const [editingFinalAgreementAmount, setEditingFinalAgreementAmount] =
    useState('');
  const messagesEndRef = useRef(null);

  // Mobile agreement sheet
  const [mobileAgreementOpen, setMobileAgreementOpen] = useState(false);
  // Add this with your other state declarations
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'images', 'details'

  // Real-time polling
  const messageIntervalRef = useRef(null);

  // Unread messages state for each proposal
  const [unreadCounts, setUnreadCounts] = useState({});

  // Find accepted offer in messages
  const findAcceptedOffer = () => {
    if (!messages || messages.length === 0) return null;
    const acceptedOfferMessage = messages.find(
      (msg) => msg.offer && msg.is_accepted === true
    );
    return acceptedOfferMessage || null;
  };

  const acceptedOffer = findAcceptedOffer();

  // Fetch proposals with unread counts
  const fetchProposals = async () => {
    try {
      const res = await serverUrlAuth.get('proposals/my-proposals/');
      setProposals(res.data);
      setFiltered(res.data);
      fetchUnreadCounts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch unread message counts for each proposal
  const fetchUnreadCounts = async (proposalsList) => {
    const counts = {};
    for (const proposal of proposalsList) {
      try {
        const res = await serverUrlAuth.get(
          `negotiations/proposal/${proposal.id}/unread-count/`
        );
        counts[proposal.id] = res.data.unread_count || 0;
      } catch (err) {
        console.error(
          `Failed to fetch unread count for proposal ${proposal.id}:`,
          err
        );
        counts[proposal.id] = 0;
      }
    }
    setUnreadCounts(counts);
  };

  // Mark messages as read
  const markMessagesAsRead = async (proposalId) => {
    try {
      await serverUrlAuth.post(
        `negotiations/proposal/${proposalId}/mark-as-read/`
      );
      setUnreadCounts((prev) => ({
        ...prev,
        [proposalId]: 0,
      }));
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  // Filter proposals
  const handleFilterChange = (e) => {
    const val = e.target.value;
    setFilter(val);
    setFiltered(
      val === 'All' ? proposals : proposals.filter((p) => p.status === val)
    );
  };

  // Refresh proposal data
  const refreshProposalData = async () => {
    if (!negotiationProposal) return;
    try {
      const res = await serverUrlAuth.get(
        `proposals/proposal/${negotiationProposal.id}/`
      );
      setNegotiationProposal(res.data);
    } catch (err) {
      console.error('Failed to refresh proposal data:', err);
    }
  };

  // Add a ref to always hold the latest negotiationProposal
  const negotiationProposalRef = useRef(null);

  useEffect(() => {
    negotiationProposalRef.current = negotiationProposal;
  }, [negotiationProposal]);

  // Updated fetchMessages and fetchFinalAgreement
  const fetchMessages = async (proposalId) => {
    if (!proposalId) return;
    try {
      const res = await serverUrlAuth.get(
        `negotiations/proposal/${proposalId}/messages/`
      );
      setMessages(res.data || []);

      // Update negotiated price if there's an accepted offer
      const acceptedOffer = res.data.find(
        (msg) => msg.offer && msg.is_accepted
      );
      if (
        acceptedOffer &&
        acceptedOffer.offer !== negotiationProposalRef.current?.negotiated_price
      ) {
        setNegotiationProposal((prev) =>
          prev ? { ...prev, negotiated_price: acceptedOffer.offer } : null
        );
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  };

  const fetchFinalAgreement = async (proposalId) => {
    if (!proposalId) return;
    try {
      const res = await serverUrlAuth.get(
        `negotiations/proposal/${proposalId}/`
      );
      setFinalAgreement(res.data);

      // Sync negotiationProposal price if different
      if (res.data.final_agreed_amount) {
        setNegotiationProposal((prev) =>
          prev
            ? { ...prev, negotiated_price: res.data.final_agreed_amount }
            : null
        );
      } else if (res.data.pending_final_amount) {
        // If there's a pending amount, show that as the current negotiated price
        setNegotiationProposal((prev) =>
          prev
            ? { ...prev, negotiated_price: res.data.pending_final_amount }
            : null
        );
      }
    } catch (err) {
      console.error('Error fetching final agreement:', err);
      setFinalAgreement(null);
    }
  };

  // Refactored polling using the ref
  const startMessagePolling = () => {
    stopMessagePolling();

    messageIntervalRef.current = setInterval(async () => {
      const currentProposal = negotiationProposalRef.current;
      if (!currentProposal) return;

      try {
        await fetchMessages(currentProposal.id);
        await fetchFinalAgreement(currentProposal.id);
      } catch (error) {
        console.error('Auto-refresh error:', error);
      }
    }, 3000); // 3 seconds
  };

  // Stop polling
  const stopMessagePolling = () => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  // UseEffect to start/stop polling when modal opens/closes
  useEffect(() => {
    if (negotiationProposal) {
      startMessagePolling();
    } else {
      stopMessagePolling();
    }

    return () => stopMessagePolling();
  }, [negotiationProposal]);

  // When opening modal, fetch initial data and start polling
  const openNegotiationModal = async (proposal) => {
    setNegotiationProposal(proposal);
    setShowFinalAgreementForm(false);
    setEditingFinalAgreement(false);
    setEditingFinalAgreementAmount('');
    setMobileAgreementOpen(false);

    // Mark messages as read
    await markMessagesAsRead(proposal.id);

    try {
      // Fetch initial data
      await fetchMessages(proposal.id);
      await fetchFinalAgreement(proposal.id);

      // Start polling (useEffect will also catch this)
      startMessagePolling();
    } catch (err) {
      console.error('Failed to fetch negotiation data:', err);
      setMessages([]);
    }
  };

  // When closing modal, stop polling
  const handleCloseNegotiationModal = () => {
    setNegotiationProposal(null);
    setShowFinalAgreementForm(false);
    setEditingFinalAgreement(false);
    setEditingFinalAgreementAmount('');
    setMobileAgreementOpen(false);
    stopMessagePolling();
    fetchUnreadCounts(proposals);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !negotiationProposal || isSending) return;

    setIsSending(true);
    try {
      const payload = {
        message: newMessage,
      };
      const res = await serverUrlAuth.post(
        `negotiations/proposal/${negotiationProposal.id}/messages/`,
        payload
      );

      // Add new message to local state immediately for better UX
      setMessages((prev) => [...prev, res.data]);
      setNewMessage('');

      // Also refresh messages to ensure we have latest from server
      await fetchMessages(negotiationProposal.id);
    } catch (err) {
      console.error('Failed to send message:', err);

      // Handle the specific restricted content error
      if (
        err.response?.data?.detail ===
        'Your message contains restricted content and cannot be delivered.'
      ) {
        toast.error(
          '⚠️ Message blocked: Contains restricted content (phone numbers, emails)'
        );
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setIsSending(false);
    }
  };

  // Handle editing final agreement
  const handleEditFinalAgreement = () => {
    if (!finalAgreement?.pending_final_amount) return;

    setEditingFinalAgreement(true);
    setEditingFinalAgreementAmount(
      finalAgreement.pending_final_amount.toString()
    );
    setShowFinalAgreementForm(true);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingFinalAgreement(false);
    setEditingFinalAgreementAmount('');
    setShowFinalAgreementForm(false);
  };

  // Submit final agreement - UPDATED FOR NEW BACKEND LOGIC
  const handleSubmitFinalAgreement = async () => {
    const amountToSubmit = editingFinalAgreement
      ? editingFinalAgreementAmount
      : finalAgreementAmount;

    if (!amountToSubmit || !negotiationProposal) return;

    setIsSubmittingFinalAgreement(true);
    try {
      const negotiationRes = await serverUrlAuth.get(
        `negotiations/proposal/${negotiationProposal.id}/`
      );
      const negotiationId = negotiationRes.data.id;

      // Use the new endpoint that sets pending_final_amount
      const res = await serverUrlAuth.patch(
        `negotiations/submit-final-agreement/${negotiationId}/`,
        {
          final_agreed_amount: parseFloat(amountToSubmit),
        }
      );

      // Update local state to show pending amount
      setNegotiationProposal((prev) =>
        prev
          ? {
              ...prev,
              negotiated_price: amountToSubmit,
            }
          : null
      );

      // Update final agreement state to show pending status
      setFinalAgreement({
        ...negotiationRes.data,
        pending_final_amount: parseFloat(amountToSubmit),
        status: 'pending',
      });

      toast.success(
        editingFinalAgreement
          ? 'Final agreement updated! Waiting for client acceptance.'
          : 'Final agreement submitted! Waiting for client acceptance.'
      );

      setFinalAgreementAmount('');
      setEditingFinalAgreementAmount('');
      setShowFinalAgreementForm(false);
      setEditingFinalAgreement(false);
      setMobileAgreementOpen(false);

      // Refresh data
      await fetchMessages(negotiationProposal.id);
      await fetchFinalAgreement(negotiationProposal.id);
      await refreshProposalData();
    } catch (err) {
      console.error('Failed to submit final agreement:', err);
      toast.error('Failed to submit final agreement');
    } finally {
      setIsSubmittingFinalAgreement(false);
    }
  };

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get unread count for a specific proposal
  const getUnreadCount = (proposalId) => {
    return unreadCounts[proposalId] || 0;
  };

  // Check if proposal has unread messages
  const hasUnreadMessages = (proposalId) => {
    return getUnreadCount(proposalId) > 0;
  };

  // Check if there's a pending final agreement waiting for client acceptance
  const hasPendingFinalAgreement = () => {
    return (
      finalAgreement?.pending_final_amount &&
      !finalAgreement?.final_agreed_amount
    );
  };

  // Check if final agreement is fully accepted
  const hasAcceptedFinalAgreement = () => {
    return finalAgreement?.final_agreed_amount && finalAgreement?.is_closed;
  };

  // Check if FAB should pulse (no accepted offer and no final agreement)
  const shouldFabPulse = () => {
    return (
      !acceptedOffer &&
      !finalAgreement?.final_agreed_amount &&
      !finalAgreement?.pending_final_amount
    );
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="my-proposals-page">
        {/* Hero */}
        <section className="hero-banner">
          <div className="hero-overlay">
            <div className="hero-content">
              <h1>📄 My Proposals</h1>
              <p>Track and manage all your submitted project proposals.</p>
            </div>
          </div>
        </section>

        {/* Filter */}
        <div className="filter-bar container">
          <label>Filter by Status:</label>
          <select value={filter} onChange={handleFilterChange}>
            <option value="All">All</option>
            <option value="Pending">Pending</option>
            <option value="Accepted">Accepted</option>
            <option value="Declined">Declined</option>
          </select>
        </div>

        {/* Proposals Grid */}
        <div className="proposals-grid container">
          {filtered.length === 0 ? (
            <p className="empty">No proposals found.</p>
          ) : (
            filtered.map((p) => (
              <div key={p.id} className="proposal-card">
                <div className="card-image">
                  <img
                    src={p.project_images?.[0] || '/placeholder.webp'}
                    alt={p.project_title}
                  />

                  {/* Status Badge */}
                  <div className="status-badge-wrapper">
                    <Badge
                      bg={
                        p.status === 'Accepted'
                          ? 'success'
                          : p.status === 'Declined'
                          ? 'danger'
                          : 'warning'
                      }
                      text={p.status === 'Pending' ? 'dark' : 'light'}
                      className="status-badge"
                    >
                      {p.status}
                    </Badge>
                  </div>

                  {/* Unread Messages Notification Badge */}
                  {hasUnreadMessages(p.id) && (
                    <div className="unread-notification-badge">
                      <Badge bg="danger" className="unread-count-badge" pill>
                        <div className="badge-content">
                          <FaComments />
                          <span className="badge-count">
                            {getUnreadCount(p.id)}
                          </span>
                        </div>
                      </Badge>
                    </div>
                  )}
                </div>

                <div className="card-content">
                  <div className="card-header">
                    <h3>{p.project_title}</h3>
                    <div className="client-info">
                      {p.client_username || 'N/A'}
                    </div>
                  </div>

                  <div className="card-details">
                    <div className="detail-item">
                      <span className="icon">💰</span>
                      <span className="label">Budget:</span>
                      <span className="value amount">
                        KSh {Number(p.project_budget).toLocaleString()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="icon">💰</span>
                      <span className="label">Proposed Amount:</span>
                      <span className="value amount">
                        KSh {Number(p.proposed_price).toLocaleString()}
                      </span>
                    </div>
                    {p.negotiated_price&&(
                      <div className="detail-item">
                      <span className="icon">💰</span>
                      <span className="label">Agreed Amount:</span>
                      <span className="value amount">
                        KSh {Number(p.negotiated_price).toLocaleString()}
                      </span>
                    </div>
                    )}
                    {p.project_description && (
                      <div className="detail-item">
                        <span className="icon">📝</span>
                        <span className="label">Description:</span>
                        <span className="value">
                          {p.project_description.slice(0, 80)}
                          {p.project_description.length > 80 ? '...' : ''}
                        </span>
                      </div>
                    )}
                    <div className="date-info">
                      <span className="label">Submitted Date</span>
                      <span className="value">
                        {new Date(p.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="card-actions">
                    <div className="action-buttons-artisan">
                      <button
                        className="btn view-btn"
                        onClick={() => {
                          setSelectedProposal(p);
                          setActiveImage(0);
                        }}
                      >
                        View Details
                      </button>

                      {/* Negotiate Button */}
                      {(p.status === 'Pending' || hasUnreadMessages(p.id)) && (
                        <div className="negotiate-button-wrapper">
                          <button
                            className={`btn negotiate-btn ${
                              hasUnreadMessages(p.id) ? 'has-unread' : ''
                            }`}
                            onClick={() => openNegotiationModal(p)}
                          >
                            💬{' '}
                            {hasUnreadMessages(p.id)
                              ? 'View Messages'
                              : 'Negotiate'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Negotiation Modal */}
        <Modal
          show={!!negotiationProposal}
          onHide={handleCloseNegotiationModal}
          centered
          className="negotiate-modal"
          fullscreen
        >
          <Modal.Header closeButton className="negotiate-header">
            <Modal.Title>💬 {negotiationProposal?.project_title}</Modal.Title>
            <div className="negotiation-info">
              <Badge
                bg={
                  negotiationProposal?.status === 'Accepted'
                    ? 'success'
                    : negotiationProposal?.status === 'Declined'
                    ? 'danger'
                    : 'warning'
                }
                text={
                  negotiationProposal?.status === 'Pending' ? 'dark' : 'light'
                }
                className="status-badge"
              >
                {negotiationProposal?.status}
              </Badge>
              {negotiationProposal?.negotiated_price && (
                <div className="proposal-amount-myproposals">
                  <span className="final-price">
                    Final Price - KSh{' '}
                    {Number(
                      negotiationProposal.negotiated_price
                    ).toLocaleString()}
                  </span>
                  {negotiationProposal.negotiated_price !==
                    negotiationProposal.proposed_price && (
                    <span className="original-price strikethrough">
                      Proposed Price - KSh{' '}
                      {Number(
                        negotiationProposal.proposed_price
                      ).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Modal.Header>

          <Modal.Body className="negotiate-body">
            {negotiationProposal && (
              <>
                {/* Mobile Tabs */}
                <div className="mobile-tabs">
                  <button
                    className={`tab-button ${
                      activeTab === 'chat' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('chat')}
                  >
                    💬 Chat
                    {messages.length > 0 && (
                      <span className="tab-badge">{messages.length}</span>
                    )}
                  </button>
                  <button
                    className={`tab-button ${
                      activeTab === 'images' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('images')}
                  >
                    🖼️ Images
                  </button>
                  <button
                    className={`tab-button ${
                      activeTab === 'details' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('details')}
                  >
                    📋 Details
                  </button>
                </div>

                {/* Desktop Three Column Layout */}
                <div className="negotiation-container three-column-layout">
                  {/* Left Column - Project Images */}
                  <div className="project-images-section">
                    <div className="images-header">
                      <h6>🖼️ Project Images</h6>
                    </div>
                    <div className="project-images-carousel">
                      {negotiationProposal.project_images &&
                      negotiationProposal.project_images.length > 0 ? (
                        <>
                          <div className="main-image-container">
                            <img
                              src={
                                negotiationProposal.project_images[
                                  activeImage
                                ] || '/placeholder.webp'
                              }
                              alt={`${negotiationProposal.project_title} - ${
                                activeImage + 1
                              }`}
                              className="main-project-image"
                            />
                          </div>
                          {negotiationProposal.project_images.length > 1 && (
                            <div className="image-thumbnails">
                              {negotiationProposal.project_images.map(
                                (image, index) => (
                                  <img
                                    key={index}
                                    src={image || '/placeholder.webp'}
                                    alt={`Thumbnail ${index + 1}`}
                                    className={`thumbnail ${
                                      activeImage === index ? 'active' : ''
                                    }`}
                                    onClick={() => setActiveImage(index)}
                                  />
                                )
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="no-images">
                          <div className="no-images-icon">📷</div>
                          <p>No project images</p>
                          <small>Client didn't upload any images</small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Middle Column - Chat Section */}
                  <div className="chat-section">
                    <div className="chat-header">
                      <h6>
                        💬 Chat with{' '}
                        {negotiationProposal.client_username || 'Client'}
                      </h6>
                      <span className="message-count">
                        {messages.length}{' '}
                        {messages.length === 1 ? 'message' : 'messages'}
                      </span>
                    </div>
                    <div className="chat-messages">
                      {messages.length === 0 ? (
                        <div className="no-messages">
                          <div className="no-messages-icon">💬</div>
                          <p>No messages yet</p>
                          <small>
                            Start the conversation with your client!
                          </small>
                        </div>
                      ) : (
                        messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`message-proposal ${
                              msg.sender_role === 'artisan'
                                ? 'artisan'
                                : 'client'
                            }`}
                          >
                            <div className="message-content">
                              <div>
                                <p>{msg.message}</p>
                                {msg.offer && (
                                  <div
                                    className={`offer-bubble ${
                                      msg.is_accepted ? 'accepted' : ''
                                    }`}
                                  >
                                    💰{' '}
                                    {msg.is_accepted
                                      ? 'Final Agreement:'
                                      : 'Offer:'}{' '}
                                    KSh {Number(msg.offer).toLocaleString()}
                                    {msg.is_accepted && (
                                      <span className="accepted-badge">
                                        ✓ Accepted
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="message-meta">
                                <span className="sender-proposals">
                                  {msg.sender_role === 'artisan'
                                    ? 'You'
                                    : negotiationProposal.client_username}
                                </span>
                                <span className="timestamp">
                                  {new Date(msg.timestamp).toLocaleTimeString(
                                    [],
                                    {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    }
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    <div className="chat-input-section">
                      <div className="chat-input">
                        <input
                          type="text"
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && handleSendMessage()
                          }
                          className="message-input"
                          disabled={isSending}
                        />
                        <button
                          onClick={handleSendMessage}
                          disabled={isSending || !newMessage.trim()}
                          className="send-btn"
                        >
                          {isSending ? '⏳' : '📤'}
                        </button>
                      </div>
 
                    </div>
                  </div>

                  {/* Right Column - Proposal & Agreement Details */}
                  <div className="proposal-details-section">
                    {/* Proposal Info Card */}
                    <div className="proposal-info-card">
                      <h6>📋 Proposal Details</h6>
                      <div className="info-item">
                        <span>Original Amount:</span>
                        <strong>
                          KSh{' '}
                          {Number(
                            negotiationProposal.proposed_price
                          ).toLocaleString()}
                        </strong>
                      </div>
                      <div className="info-item">
                        <span>Current Amount:</span>
                        <strong>
                          KSh{' '}
                          {Number(
                            negotiationProposal.negotiated_price ||
                              negotiationProposal.proposed_price
                          ).toLocaleString()}
                        </strong>
                      </div>
                      <div className="info-item">
                        <span>Status:</span>
                        <Badge
                          bg={
                            negotiationProposal.status === 'Accepted'
                              ? 'success'
                              : negotiationProposal.status === 'Declined'
                              ? 'danger'
                              : 'warning'
                          }
                          text={
                            negotiationProposal.status === 'Pending'
                              ? 'dark'
                              : 'light'
                          }
                          className="status-badge-sm"
                        >
                          {negotiationProposal.status}
                        </Badge>
                      </div>
                      <div className="info-item">
                        <span>Client:</span>
                        <strong>
                          {negotiationProposal.client_username || 'N/A'}
                        </strong>
                      </div>
                      <div className="info-item">
                        <span>Submitted:</span>
                        <strong>
                          {new Date(
                            negotiationProposal.created_at
                          ).toLocaleDateString()}
                        </strong>
                      </div>
                    </div>

                    {/* Final Agreement Card */}
                    <div
                      className={`agreement-card ${
                        hasAcceptedFinalAgreement()
                          ? 'success'
                          : hasPendingFinalAgreement()
                          ? 'pending'
                          : showFinalAgreementForm || editingFinalAgreement
                          ? 'form-visible'
                          : ''
                      }`}
                    >
                      <h6>🤝 Final Agreement</h6>

                      {hasAcceptedFinalAgreement() ? (
                        <div className="agreement-details">
                          <p>
                            <strong>Final Agreed Amount:</strong> KSh{' '}
                            {Number(
                              finalAgreement.final_agreed_amount
                            ).toLocaleString()}
                          </p>
                          <p>
                            <strong>Status:</strong>{' '}
                            <Badge bg="success">Accepted</Badge>
                          </p>
                        </div>
                      ) : hasPendingFinalAgreement() &&
                        !showFinalAgreementForm &&
                        !editingFinalAgreement ? (
                        <div className="agreement-details">
                          <div className="agreement-header-with-actions">
                            <div>
                              <p>
                                <strong>Pending Amount:</strong> KSh{' '}
                                {Number(
                                  finalAgreement.pending_final_amount
                                ).toLocaleString()}
                              </p>
                              <p>
                                <strong>Status:</strong>{' '}
                                <Badge bg="warning">Waiting for Client</Badge>
                              </p>
                              <p>
                                Your final agreement proposal has been sent to
                                the client and is awaiting their acceptance.
                              </p>
                            </div>
                            {/* Add Edit Button Here */}
                            <button
                              onClick={handleEditFinalAgreement}
                              className="edit-final-agreement-btn"
                              title="Edit the final amount"
                            >
                              ✏️ Edit Final Agreed Amount
                            </button>
                          </div>
                        </div>
                      ) : showFinalAgreementForm || editingFinalAgreement ? (
                        <div className="agreement-input-group">
                          <div className="agreement-input-wrapper">
                            <span className="currency-symbol">KSh</span>
                            <input
                              type="number"
                              placeholder="Enter final amount..."
                              value={
                                editingFinalAgreement
                                  ? editingFinalAgreementAmount
                                  : finalAgreementAmount
                              }
                              onChange={(e) =>
                                editingFinalAgreement
                                  ? setEditingFinalAgreementAmount(
                                      e.target.value
                                    )
                                  : setFinalAgreementAmount(e.target.value)
                              }
                              className="agreement-input"
                              min="1"
                              step="0.01"
                            />
                          </div>
                          <div className="agreement-form-actions">
                            <button
                              onClick={handleSubmitFinalAgreement}
                              disabled={
                                (!editingFinalAgreement &&
                                  !finalAgreementAmount) ||
                                (editingFinalAgreement &&
                                  !editingFinalAgreementAmount) ||
                                isSubmittingFinalAgreement
                              }
                              className="submit-agreement-btn"
                            >
                              {isSubmittingFinalAgreement
                                ? 'Submitting...'
                                : editingFinalAgreement
                                ? 'Update Final Agreement'
                                : 'Submit Final Agreement'}
                            </button>
                            <button
                              onClick={
                                editingFinalAgreement
                                  ? handleCancelEdit
                                  : () => {
                                      setShowFinalAgreementForm(false);
                                      setFinalAgreementAmount('');
                                    }
                              }
                              className="cancel-agreement-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p>
                            Ready to finalize the agreement? Submit your final
                            price to the client for acceptance.
                          </p>
                          <button
                            onClick={() => setShowFinalAgreementForm(true)}
                            className="show-agreement-form-btn"
                          >
                            💰 Submit Final Agreement
                          </button>
                        </>
                      )}

                      <small>
                        {hasAcceptedFinalAgreement()
                          ? 'This agreement has been accepted by both parties.'
                          : hasPendingFinalAgreement()
                          ? 'The client will need to accept this amount to finalize the agreement.'
                          : 'The client will need to accept your final amount to proceed.'}
                      </small>
                    </div>
                  </div>
                </div>

                {/* Mobile Content Sections */}
                <div className="mobile-content-sections">
                  {/* Chat Tab Content */}
                  {activeTab === 'chat' && (
                    <div className="mobile-tab-content chat-tab">
                      {/* <div className="mobile-chat-header">
                        <h6>
                          💬 Chat with{' '}
                          {negotiationProposal.client_username || 'Client'}
                        </h6>
                        <span className="message-count">
                          {messages.length}{' '}
                          {messages.length === 1 ? 'message' : 'messages'}
                        </span>
                      </div> */}
                      <div className="mobile-chat-messages">
                        {messages.length === 0 ? (
                          <div className="no-messages">
                            <div className="no-messages-icon">💬</div>
                            <p>No messages yet</p>
                            <small>
                              Start the conversation with your client!
                            </small>
                          </div>
                        ) : (
                          messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`message-proposal ${
                                msg.sender_role === 'artisan'
                                  ? 'artisan'
                                  : 'client'
                              }`}
                            >
                              <div className="message-content">
                                <div>
                                  <p>{msg.message}</p>
                                  {msg.offer && (
                                    <div
                                      className={`offer-bubble ${
                                        msg.is_accepted ? 'accepted' : ''
                                      }`}
                                    >
                                      💰{' '}
                                      {msg.is_accepted
                                        ? 'Final Agreement:'
                                        : 'Offer:'}{' '}
                                      KSh {Number(msg.offer).toLocaleString()}
                                      {msg.is_accepted && (
                                        <span className="accepted-badge">
                                          ✓ Accepted
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="message-meta">
                                  <span className="sender-proposals">
                                    {msg.sender_role === 'artisan'
                                      ? 'You'
                                      : negotiationProposal.client_username}
                                  </span>
                                  <span className="timestamp">
                                    {new Date(msg.timestamp).toLocaleTimeString(
                                      [],
                                      {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      }
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                      <div className="mobile-chat-input">
                        <div className="chat-input">
                          <input
                            type="text"
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) =>
                              e.key === 'Enter' && handleSendMessage()
                            }
                            className="message-input"
                            disabled={isSending}
                          />
                          <button
                            onClick={handleSendMessage}
                            disabled={isSending || !newMessage.trim()}
                            className="send-btn"
                          >
                            {isSending ? '⏳' : '📤'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Images Tab Content */}
                  {activeTab === 'images' && (
                    <div className="mobile-tab-content images-tab">
                      <div className="mobile-images-header">
                        <h6>Project Images</h6>
                      </div>
                      <div className="mobile-images-carousel">
                        {negotiationProposal.project_images &&
                        negotiationProposal.project_images.length > 0 ? (
                          <>
                            <div className="mobile-main-image-container">
                              <img
                                src={
                                  negotiationProposal.project_images[
                                    activeImage
                                  ] || '/placeholder.webp'
                                }
                                alt={`${negotiationProposal.project_title} -  ${
                                  activeImage + 1
                                }`}
                                className="mobile-main-project-image"
                              />
                            </div>
                            {negotiationProposal.project_images.length > 1 && (
                              <div className="mobile-image-thumbnails">
                                {negotiationProposal.project_images.map(
                                  (image, index) => (
                                    <img
                                      key={index}
                                      src={image || '/placeholder.webp'}
                                      alt={`Thumbnail ${index + 1}`}
                                      className={`mobile-thumbnail ${
                                        activeImage === index ? 'active' : ''
                                      }`}
                                      onClick={() => setActiveImage(index)}
                                    />
                                  )
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="mobile-no-images">
                            <div className="no-images-icon">📷</div>
                            <p>No project images</p>
                            <small>Client didn't upload any images</small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Details Tab Content */}
                  {activeTab === 'details' && (
                    <div className="mobile-tab-content details-tab">
                      <div className="mobile-details-content">
                        {/* Proposal Info Card */}
                        <div className="proposal-info-card">
                          <h6>📋 Proposal Details</h6>
                          <div className="info-item">
                            <span>Project Title:</span>
                            <strong>{negotiationProposal.project_title}</strong>
                          </div>
                          <div className="info-item">
                            <span>Original Amount:</span>
                            <strong className="amount">
                              KSh{' '}
                              {Number(
                                negotiationProposal.proposed_price
                              ).toLocaleString()}
                            </strong>
                          </div>
                          <div className="info-item">
                            <span>Agreed Amount:</span>
                            <strong className="amount">
                              KSh{' '}
                              {Number(
                                negotiationProposal.negotiated_price ||
                                  negotiationProposal.proposed_price
                              ).toLocaleString()}
                            </strong>
                          </div>
                          <div className="info-item">
                            <span>Status:</span>
                            <Badge
                              bg={
                                negotiationProposal.status === 'Accepted'
                                  ? 'success'
                                  : negotiationProposal.status === 'Declined'
                                  ? 'danger'
                                  : 'warning'
                              }
                              text={
                                negotiationProposal.status === 'Pending'
                                  ? 'dark'
                                  : 'light'
                              }
                              className="status-badge-sm"
                            >
                              {negotiationProposal.status}
                            </Badge>
                          </div>
                          <div className="info-item">
                            <span>Client:</span>
                            <strong>
                              {negotiationProposal.client_username || 'N/A'}
                            </strong>
                          </div>
                          <div className="info-item">
                            <span>Submitted:</span>
                            <strong>
                              {new Date(
                                negotiationProposal.created_at
                              ).toLocaleDateString()}
                            </strong>
                          </div>
                        </div>

                        {/* Project Description Card */}
                        {negotiationProposal.project_description && (
                          <div className="project-description-card">
                            <h6>📝 Project Description</h6>
                            <div className="description-content">
                              {negotiationProposal.project_description}
                            </div>
                          </div>
                        )}

                        {/* Proposal Note Card */}
                        {negotiationProposal.note && (
                          <div className="proposal-note-card">
                            <h6>💡 Your Note</h6>
                            <div className="note-content">
                              {negotiationProposal.note}
                            </div>
                          </div>
                        )}

                        {/* Final Agreement Card */}
                        <div
                          className={`agreement-card ${
                            hasAcceptedFinalAgreement()
                              ? 'success'
                              : hasPendingFinalAgreement()
                              ? 'pending'
                              : showFinalAgreementForm || editingFinalAgreement
                              ? 'form-visible'
                              : ''
                          }`}
                        >
                          <h6>🤝 Final Agreement</h6>

                          {hasAcceptedFinalAgreement() ? (
                            <div className="agreement-details">
                              <p>
                                <strong>Final Agreed Amount:</strong> KSh{' '}
                                {Number(
                                  finalAgreement.final_agreed_amount
                                ).toLocaleString()}
                              </p>
                              <p>
                                <strong>Status:</strong>{' '}
                                <Badge bg="success">Accepted</Badge>
                              </p>
                              <p>
                                <strong>Agreed on:</strong>{' '}
                                {new Date(
                                  finalAgreement.updated_at
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          ) : hasPendingFinalAgreement() &&
                            !showFinalAgreementForm &&
                            !editingFinalAgreement ? (
                            <div className="agreement-details">
                              <div className="agreement-header-with-actions">
                                <div>
                                  <p>
                                    <strong>Pending Amount:</strong> KSh{' '}
                                    {Number(
                                      finalAgreement.pending_final_amount
                                    ).toLocaleString()}
                                  </p>
                                  <p>
                                    <strong>Status:</strong>{' '}
                                    <Badge bg="warning">
                                      Waiting for Client
                                    </Badge>
                                  </p>
                                  <p>
                                    Your final agreement proposal has been sent
                                    to the client and is awaiting their
                                    acceptance.
                                  </p>
                                </div>
                                {/* Edit Button for Mobile */}
                                <button
                                  onClick={handleEditFinalAgreement}
                                  className="edit-final-agreement-btn mobile"
                                  title="Edit the final amount"
                                >
                                  ✏️ Edit
                                </button>
                              </div>
                            </div>
                          ) : showFinalAgreementForm ||
                            editingFinalAgreement ? (
                            <div className="agreement-input-group">
                              <div className="agreement-input-wrapper">
                                <span className="currency-symbol">KSh</span>
                                <input
                                  type="number"
                                  placeholder="Enter final amount..."
                                  value={
                                    editingFinalAgreement
                                      ? editingFinalAgreementAmount
                                      : finalAgreementAmount
                                  }
                                  onChange={(e) =>
                                    editingFinalAgreement
                                      ? setEditingFinalAgreementAmount(
                                          e.target.value
                                        )
                                      : setFinalAgreementAmount(e.target.value)
                                  }
                                  className="agreement-input"
                                  min="1"
                                  step="0.01"
                                />
                              </div>
                              <div className="agreement-form-actions">
                                <button
                                  onClick={handleSubmitFinalAgreement}
                                  disabled={
                                    (!editingFinalAgreement &&
                                      !finalAgreementAmount) ||
                                    (editingFinalAgreement &&
                                      !editingFinalAgreementAmount) ||
                                    isSubmittingFinalAgreement
                                  }
                                  className="submit-agreement-btn"
                                >
                                  {isSubmittingFinalAgreement
                                    ? '⏳ Submitting...'
                                    : editingFinalAgreement
                                    ? '✅ Update Final Agreement'
                                    : '✅ Submit Final Agreement'}
                                </button>
                                <button
                                  onClick={
                                    editingFinalAgreement
                                      ? handleCancelEdit
                                      : () => {
                                          setShowFinalAgreementForm(false);
                                          setFinalAgreementAmount('');
                                        }
                                  }
                                  className="cancel-agreement-btn"
                                >
                                  ❌ Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p>
                                Ready to finalize the agreement? Submit your
                                final price to the client for acceptance.
                              </p>
                              <button
                                onClick={() => setShowFinalAgreementForm(true)}
                                className="show-agreement-form-btn"
                              >
                                💰 Submit Final Agreement
                              </button>
                            </>
                          )}

                          <small>
                            {hasAcceptedFinalAgreement()
                              ? 'This agreement has been accepted by both parties.'
                              : hasPendingFinalAgreement()
                              ? 'The client will need to accept this amount to finalize the agreement.'
                              : 'The client will need to accept your final amount to proceed.'}
                          </small>
                        </div>

                        {/* Quick Actions Card */}
                        <div className="action-buttons-card">
                          <h6>⚡ Quick Actions</h6>
                          <div className="mobile-actions">
                            <button
                              className="mobile-action-btn"
                              onClick={() => {
                                setActiveTab('chat');
                                // Auto-focus on message input when switching to chat
                                setTimeout(() => {
                                  const messageInput =
                                    document.querySelector('.message-input');
                                  if (messageInput) messageInput.focus();
                                }, 300);
                              }}
                            >
                              💬 Continue Chatting
                            </button>
                            <button
                              className="mobile-action-btn secondary"
                              onClick={() => {
                                setActiveTab('images');
                              }}
                            >
                              🖼️ View Images
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer className="negotiate-footer">
            {/* <Button
              variant="outline-secondary"
              onClick={handleCloseNegotiationModal}
            >
              Close Chat
            </Button> */}
            <div className="communication-guideline">
              <small className="text-muted">
                <i className="bi bi-shield-check me-1"></i>
                By sending messages, you agree to our{' '}
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  Terms and Conditions
                </a>
                . Keep all communication project-related and avoid sharing
                personal contact information. All messages are recorded for
                project documentation.
              </small>
            </div>
          </Modal.Footer>
        </Modal>

        {/* View Details Modal */}
        <Modal
          show={!!selectedProposal}
          onHide={() => setSelectedProposal(null)}
          size="lg"
          centered
          className="view-details-modal"
          backdrop="static"
        >
          <Modal.Header closeButton className="view-details-header">
            <Modal.Title>📋 Proposal Details</Modal.Title>
            <div className="header-actions">
              <Badge
                bg={
                  selectedProposal?.status === 'Accepted'
                    ? 'success'
                    : selectedProposal?.status === 'Declined'
                    ? 'danger'
                    : 'warning'
                }
                text={selectedProposal?.status === 'Pending' ? 'dark' : 'light'}
                className="status-badge"
              >
                {selectedProposal?.status}
              </Badge>
            </div>
          </Modal.Header>

          <Modal.Body className="view-details-body">
            {selectedProposal && (
              <div className="proposal-details-container">
                {/* Image Carousel */}
                {selectedProposal.project_images &&
                selectedProposal.project_images.length > 0 ? (
                  <div className="image-carousel-section">
                    <Carousel
                      activeIndex={activeImage}
                      onSelect={setActiveImage}
                      interval={null}
                      className="proposal-carousel"
                      prevIcon={
                        <span
                          className="carousel-control-prev-icon"
                          aria-hidden="true"
                        />
                      }
                      nextIcon={
                        <span
                          className="carousel-control-next-icon"
                          aria-hidden="true"
                        />
                      }
                    >
                      {selectedProposal.project_images.map((image, index) => (
                        <Carousel.Item key={index}>
                          <div className="carousel-image-container">
                            <img
                              src={image || '/placeholder.webp'}
                              alt={`${selectedProposal.project_title} -  ${
                                index + 1
                              }`}
                              className="carousel-image"
                              onError={(e) => {
                                e.target.src = '/placeholder.webp';
                              }}
                            />
                          </div>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                    <div className="carousel-indicator">
                      📷 Image {activeImage + 1} of{' '}
                      {selectedProposal.project_images.length}
                    </div>
                  </div>
                ) : (
                  <div className="image-carousel-section">
                    <div className="no-images-placeholder">
                      <div className="no-images-icon">📷</div>
                      <h5>No Project Images</h5>
                      <p>
                        The client didn't upload any images for this project
                      </p>
                    </div>
                  </div>
                )}

                {/* Proposal Information */}
                <div className="proposal-info-section">
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">🏷️ Project Title</span>
                      <span className="value">
                        {selectedProposal.project_title}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="label">👤 Client</span>
                      <span className="value">
                        {selectedProposal.client_username || 'Not specified'}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="label">💰 Proposed Amount</span>
                      <span className="value amount">
                        KSh{' '}
                        {Number(
                          selectedProposal.proposed_price
                        ).toLocaleString()}
                      </span>
                    </div>

                    {selectedProposal.negotiated_price &&
                      selectedProposal.negotiated_price !==
                        selectedProposal.proposed_price && (
                        <div className="info-item">
                          <span className="label">🤝 Agreed Amount</span>
                          <span className="value amount negotiated">
                            KSh{' '}
                            {Number(
                              selectedProposal.negotiated_price
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}

                    <div className="info-item">
                      <span className="label">📅 Submitted Date</span>
                      <span className="value">
                        {new Date(
                          selectedProposal.created_at
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          weekday: 'long',
                        })}
                      </span>
                    </div>

                    {selectedProposal.updated_at && (
                      <div className="info-item">
                        <span className="label">🔄 Last Updated</span>
                        <span className="value">
                          {new Date(
                            selectedProposal.updated_at
                          ).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Project Description */}
                  {selectedProposal.project_description && (
                    <div className="description-section">
                      <h6>📝 Project Description</h6>
                      <div className="description-content">
                        {selectedProposal.project_description}
                      </div>
                    </div>
                  )}

                  {/* Artisan's Note */}
                  {selectedProposal.note && (
                    <div className="note-section">
                      <h6>💡 Your Proposal Note</h6>
                      <div className="note-content">
                        {selectedProposal.note}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="view-details-footer">
            <Button
              variant="secondary"
              onClick={() => setSelectedProposal(null)}
              className="close-btn"
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default withAuth(MyProposals, 'artisan');
